/**
 * Classify a failed request as "the backend is not available" (as opposed to
 * "the record does not exist / you lack access").
 *
 * A record screen must not show "Sorry, we cannot find what you're looking for"
 * when the backend is merely down or restarting (e.g. during an update): that
 * message reads as if the record was deleted.
 *
 * Unavailable = HTTP 502/503/504, a request timeout, or a network error with no
 * HTTP response at all. A cancelled request is not an outage.
 */
export const BACKEND_UNAVAILABLE_STATUSES = [502, 503, 504];

const NO_RESPONSE_CODES = ["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK"];

export const isBackendUnavailableError = (error: unknown): boolean => {
    if (!error || typeof error !== "object") {
        return false;
    }
    const err = error as {
        response?: { status?: number };
        code?: string;
        name?: string;
        isAxiosError?: boolean;
        __CANCEL__?: boolean;
    };
    if (err.__CANCEL__ || err.code === "ERR_CANCELED" || err.name === "CanceledError") {
        return false;
    }
    const status = err.response?.status;
    if (typeof status === "number") {
        return BACKEND_UNAVAILABLE_STATUSES.includes(status);
    }
    if (err.code && NO_RESPONSE_CODES.includes(err.code)) {
        return true;
    }
    return !!err.isAxiosError && !err.response;
};

/** Rejection value a record fetch uses when the backend is not available. */
export interface BackendUnavailableRejection {
    backendUnavailable: true;
    status?: number;
}

export const toBackendUnavailableRejection = (error: unknown): BackendUnavailableRejection => ({
    backendUnavailable: true,
    status: (error as { response?: { status?: number } })?.response?.status,
});

export const isBackendUnavailableRejection = (
    value: unknown
): value is BackendUnavailableRejection =>
    !!value &&
    typeof value === "object" &&
    (value as BackendUnavailableRejection).backendUnavailable === true;

/** Auto-retry delay for the n-th retry (0-based): 5 s, 10 s, 20 s, then every 30 s. */
export const backendRetryDelayMs = (attempt: number): number =>
    Math.min(5000 * 2 ** Math.max(0, attempt), 30000);
